import { Injectable, inject } from '@angular/core';
import { deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, verifyBeforeUpdateEmail } from 'firebase/auth';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

import { User } from '../models/user.model';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);

  async getCurrentUserDocument(forceFetch = false): Promise<User> {
    const inMemoryUser = this.dataStore.getProperty('currentUser');
    if (!forceFetch && inMemoryUser && inMemoryUser.userId) {
      return inMemoryUser;
    }

    await this.firebase.auth.authStateReady();
    const authUser = this.firebase.auth.currentUser;
    if (!authUser) {
      throw new Error('User not found');
    }

    const userDoc = await getDoc(doc(this.firebase.globalFirestore, `users/${authUser.uid}`));
    const currentUser = userDoc.data() as User;
    if (!currentUser.userId) {
      currentUser.userId = authUser.uid;
    }
    this.dataStore.updateState({ currentUser });

    return currentUser;
  }

  async getCurrentAccountId(): Promise<string> {
    const { currentAccountId, defaultAccountId } = await this.getCurrentUserDocument();
    return currentAccountId || defaultAccountId || '';
  }

  async getDefaultAccountId(): Promise<string> {
    const { defaultAccountId } = await this.getCurrentUserDocument();
    if (!defaultAccountId) {
      throw new Error('Default account ID not found');
    }
    return defaultAccountId;
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');
    await updateProfile(user, { displayName });
  }

  async hasAcceptedLicense(): Promise<boolean> {
    const currentUser = await this.getCurrentUserDocument(true);
    return currentUser.licenseAccepted;
  }

  async updateEmail(email: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');
    await verifyBeforeUpdateEmail(user, email);
  }

  async updateThemePreference(theme: 'light' | 'dark'): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');
    await setDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), { theme }, { merge: true });
  }

  async getThemePreference(): Promise<'light' | 'dark' | undefined> {
    const currentUser = await this.getCurrentUserDocument();
    return currentUser?.theme;
  }

  /**
   * The model family chosen during onboarding, if any. Create is the first app
   * to honour it — chat-ui writes its own last-used model and never reads this.
   * Failures are swallowed: a preference is a nicety, and a picker that throws
   * because one document could not be read is not.
   */
  async getModelFamilyPreference(): Promise<string | undefined> {
    const currentUser = await this.getCurrentUserDocument().catch(() => undefined);
    return currentUser?.preferences?.modelFamily;
  }

  async getLanguagePreference(): Promise<string | undefined> {
    const currentUser = await this.getCurrentUserDocument();
    return currentUser?.language;
  }

  async updateLanguagePreference(language: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');
    await setDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), { language }, { merge: true });
  }

  async uploadProfilePicture(file: File): Promise<string> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');

    const currentUser = await this.getCurrentUserDocument();
    const oldStoragePath = currentUser.profilePictureStoragePath;

    const ext = file.name.split('.').pop() ?? 'png';
    const storagePath = `users/${user.uid}/profile-picture/${Date.now()}.${ext}`;
    const storageRef = ref(this.firebase.globalStorage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, { contentType: file.type });

    await new Promise<void>((resolve, reject) => {
      uploadTask.on('state_changed', null, reject, () => resolve());
    });

    const downloadUrl = await getDownloadURL(storageRef);
    await setDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), {
      profilePictureUrl: downloadUrl,
      profilePictureStoragePath: storagePath,
    }, { merge: true });

    if (oldStoragePath && oldStoragePath !== storagePath) {
      await deleteObject(ref(this.firebase.globalStorage, oldStoragePath)).catch(() => { /* best-effort cleanup */ });
    }

    await updateProfile(user, { photoURL: downloadUrl }).catch(() => { /* non-fatal */ });

    currentUser.profilePictureUrl = downloadUrl;
    currentUser.profilePictureStoragePath = storagePath;
    this.dataStore.updateState({ currentUser: { ...currentUser } });
    return downloadUrl;
  }

  async removeProfilePicture(): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) throw new Error('User not found');

    const currentUser = await this.getCurrentUserDocument();
    const storagePath = currentUser.profilePictureStoragePath;

    await updateDoc(doc(this.firebase.globalFirestore, `users/${user.uid}`), {
      profilePictureUrl: deleteField(),
      profilePictureStoragePath: deleteField(),
    });

    if (storagePath) {
      await deleteObject(ref(this.firebase.globalStorage, storagePath)).catch(() => { /* best-effort cleanup */ });
    }

    await updateProfile(user, { photoURL: null }).catch(() => { /* non-fatal */ });

    delete currentUser.profilePictureUrl;
    delete currentUser.profilePictureStoragePath;
    this.dataStore.updateState({ currentUser: { ...currentUser } });
  }

  async addOrganizationToUser(userId: string, organizationId: string): Promise<void> {
    const { arrayUnion } = await import('firebase/firestore');
    const userRef = doc(this.firebase.globalFirestore, 'users', userId);
    await updateDoc(userRef, {
      organizationIds: arrayUnion(organizationId),
    });
  }
}
